import { WorkOrderSortKey } from '@atlas/types'
import { SortDirection } from '@atlas/ui-components'
import { statusLabel } from '@/domain/work-order.types'
import { WorkflowPhase } from '@/domain/workflow.types'
import { useCan } from '@/lib/capabilities'
import type { ReviewQueueRow } from '@/pages/Dashboard/ReviewQueueCard'
import type { WorkOrderBoardPage } from '@/services/work-order.service'
import { useWorkOrderBoard } from './useWorkOrders'

const waitHoursSince = (iso: string): number => Math.max(0, (Date.now() - new Date(iso).getTime()) / 3_600_000)

function toRows({ workOrders, workflows }: WorkOrderBoardPage): ReviewQueueRow[] {
	const labelOf = (workflowId: string | null, code: string) =>
		workflows.find((wf) => wf.workflowId === workflowId)?.statuses.find((s) => s.code === code)?.label ??
		statusLabel(code)
	return workOrders.map((workOrder) => ({
		key: workOrder.workOrderId,
		href: `/work-orders/${workOrder.workOrderId}`,
		code: workOrder.orderCode,
		title: workOrder.title,
		stageLabel: labelOf(workOrder.workflowId, workOrder.status),
		waitHours: waitHoursSince(workOrder.statusChangedAt),
	}))
}

/**
 * What is waiting on a reviewer: every work order on a validation-phase status,
 * whoever it is assigned to, longest wait first. The phase is the status's
 * configuration, so a lane an administrator adds or renames joins the queue.
 *
 * Reviewing is ADMIN and above; below that there is no queue and nothing is
 * fetched. `rows` holds at most `limit` items, and `total` counts all of them.
 */
export function useMyReviews(limit: number) {
	const { canConfigure: canReview } = useCan()
	const query = useWorkOrderBoard(
		{
			'filters[phase]': WorkflowPhase.VALIDATION,
			sort: WorkOrderSortKey.STATUS_CHANGED_AT,
			order: SortDirection.ASC,
			limit,
		},
		{ enabled: canReview },
	)
	return {
		canReview,
		rows: query.data ? toRows(query.data) : [],
		total: query.data?.total ?? 0,
		isLoading: canReview && query.isLoading,
	}
}
